import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, CreditCard, Plus, Trash2, AlertCircle, Mail, 
  Loader2, RefreshCw, ExternalLink, AlertTriangle, Tv,
  Music, Smartphone, Dumbbell, ShieldCheck, Newspaper
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const categoryIcons = {
  streaming: Tv,
  musique: Music,
  telecom: Smartphone,
  sport: Dumbbell,
  assurance: ShieldCheck,
  media: Newspaper,
  tech: Smartphone,
  shopping: CreditCard,
  autre: CreditCard
};

export const AbonnementsPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [potentialSavings, setPotentialSavings] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSub, setNewSub] = useState({ name: '', amount: '', category: 'autre' });

  // Check URL params for Gmail connection status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_connected') === 'true') {
      toast.success('Gmail connecté avec succès !');
      setGmailConnected(true);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('gmail_error')) {
      toast.error('Erreur de connexion Gmail');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Load subscriptions
  useEffect(() => {
    const loadSubscriptions = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptions(response.data.subscriptions || []);
        setDuplicates(response.data.duplicates || []);
        setTotalCost(response.data.total_monthly_cost || 0);
        setPotentialSavings(response.data.potential_savings || 0);
      } catch (error) {
        console.error('Error loading subscriptions:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) loadSubscriptions();
  }, [token]);

  const handleConnectGmail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/oauth/gmail/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Erreur lors de la connexion Gmail');
    }
  };

  const handleScanGmail = async () => {
    setScanning(true);
    try {
      const response = await axios.post(`${API_URL}/api/subscriptions/scan`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSubscriptions(response.data.subscriptions || []);
        setDuplicates(response.data.duplicates || []);
        setTotalCost(response.data.total_monthly_cost || 0);
        setPotentialSavings(response.data.potential_savings_monthly || 0);
        toast.success(`${response.data.total_count} abonnements détectés !`);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Connecte d\'abord ton Gmail');
      } else {
        toast.error('Erreur lors du scan');
      }
    } finally {
      setScanning(false);
    }
  };

  const handleDeleteSubscription = async (serviceKey) => {
    try {
      await axios.delete(`${API_URL}/api/subscriptions/${serviceKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(prev => prev.filter(s => s.service_key !== serviceKey));
      toast.success('Abonnement supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddManual = async () => {
    if (!newSub.name || !newSub.amount) {
      toast.error('Remplis tous les champs');
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/manual`,
        null,
        {
          params: {
            name: newSub.name,
            amount: parseFloat(newSub.amount),
            category: newSub.category
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSubscriptions(prev => [...prev, response.data.subscription]);
      setAddDialogOpen(false);
      setNewSub({ name: '', amount: '', category: 'autre' });
      toast.success('Abonnement ajouté');
      // Recharger pour recalculer les totaux
      loadSubscriptionsData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleSeedDemo = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/seed-demo`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`${response.data.count} abonnements de démo ajoutés !`);
        loadSubscriptionsData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la création des données de démo');
    }
  };

  const loadSubscriptionsData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data.subscriptions || []);
      setDuplicates(response.data.duplicates || []);
      setTotalCost(response.data.total_monthly_cost || 0);
      setPotentialSavings(response.data.potential_savings || 0);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const Icon = (category) => categoryIcons[category] || CreditCard;

  return (
    <div className={`min-h-screen px-4 py-6 transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`} data-testid="abonnements-page">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/dashboard" 
          className={`inline-flex items-center gap-2 mb-6 transition-colors ${
            isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
          }`}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#81d8d0]/20 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-[#81d8d0]" />
            </div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Mes Abonnements
            </h1>
          </div>
          <p className={`mb-8 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
            Connecte ton Gmail pour détecter automatiquement tes abonnements
          </p>
        </motion.div>

        {/* Summary cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
        >
          <Card className={`p-5 border ${isDark ? 'bg-[#81d8d0]/10 border-[#81d8d0]/20' : 'bg-[#81d8d0]/5 border-[#81d8d0]/30'}`}>
            <p className={`text-sm mb-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Total mensuel</p>
            <p className="text-3xl font-bold text-[#81d8d0]">{totalCost.toFixed(2)}€</p>
          </Card>
          <Card className={`p-5 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
            <p className={`text-sm mb-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Abonnements actifs</p>
            <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{subscriptions.length}</p>
          </Card>
          <Card className={`p-5 border ${isDark ? 'bg-[#FF6B6B]/10 border-[#FF6B6B]/20' : 'bg-[#FF6B6B]/5 border-[#FF6B6B]/30'}`}>
            <p className={`text-sm mb-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Économies possibles</p>
            <p className="text-3xl font-bold text-[#FF6B6B]">{potentialSavings.toFixed(2)}€</p>
          </Card>
        </motion.div>

        {/* Gmail Connect / Scan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
        >
          <Card className={`p-6 border ${
            isDark 
              ? 'bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20' 
              : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'
          }`}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-white'}`}>
                  <Mail className={`w-7 h-7 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                </div>
                <div>
                  <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Détection automatique via Gmail
                  </h3>
                  <p className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>
                    Skipya analyse tes emails de facturation pour détecter tous tes abonnements
                  </p>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                {subscriptions.length === 0 && (
                  <Button
                    onClick={handleSeedDemo}
                    variant="outline"
                    className={`border-purple-500/50 ${isDark ? 'text-purple-400 hover:bg-purple-500/10' : 'text-purple-600 hover:bg-purple-50'}`}
                    data-testid="seed-demo"
                  >
                    ✨ Charger démo
                  </Button>
                )}
                <Button
                  onClick={handleConnectGmail}
                  variant="outline"
                  className={`border-blue-500/50 ${isDark ? 'text-blue-400 hover:bg-blue-500/10' : 'text-blue-600 hover:bg-blue-50'}`}
                  data-testid="connect-gmail"
                >
                  <Mail size={18} className="mr-2" />
                  Connecter Gmail
                </Button>
                <Button
                  onClick={handleScanGmail}
                  disabled={scanning}
                  className="bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A] font-semibold"
                  data-testid="scan-gmail"
                >
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw size={18} className="mr-2" />}
                  Scanner
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Duplicates Warning */}
        {duplicates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className={`p-5 border ${isDark ? 'bg-[#FF6B6B]/10 border-[#FF6B6B]/30' : 'bg-[#FF6B6B]/5 border-[#FF6B6B]/30'}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#FF6B6B] mt-0.5" />
                <div>
                  <h4 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Abonnements en doublon détectés !
                  </h4>
                  {duplicates.map((dup, i) => (
                    <p key={i} className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                      {dup.suggestion} <span className="text-[#FF6B6B] font-medium">({dup.potential_savings.toFixed(2)}€/mois d'économie)</span>
                    </p>
                  ))}
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Subscriptions List */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Abonnements détectés
            </h2>
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className={isDark ? 'border-white/20 text-white' : ''}>
                  <Plus size={16} className="mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent className={isDark ? 'bg-[#1A1A2E] border-white/10' : ''}>
                <DialogHeader>
                  <DialogTitle className={isDark ? 'text-white' : ''}>Ajouter un abonnement</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input
                    placeholder="Nom (ex: Netflix)"
                    value={newSub.name}
                    onChange={(e) => setNewSub({ ...newSub, name: e.target.value })}
                    className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
                  />
                  <Input
                    type="number"
                    placeholder="Montant mensuel (€)"
                    value={newSub.amount}
                    onChange={(e) => setNewSub({ ...newSub, amount: e.target.value })}
                    className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
                  />
                  <Button onClick={handleAddManual} className="w-full bg-[#81d8d0] text-[#0F0F1A]">
                    Ajouter
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#81d8d0]" />
            </div>
          ) : subscriptions.length === 0 ? (
            <Card className={`p-12 text-center border border-dashed ${
              isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-300'
            }`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isDark ? 'bg-[#81d8d0]/10' : 'bg-[#81d8d0]/20'
              }`}>
                <CreditCard className="w-8 h-8 text-[#81d8d0]" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Aucun abonnement détecté
              </h3>
              <p className={`mb-6 max-w-md mx-auto ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                Connecte ton Gmail ou ajoute manuellement tes abonnements
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {subscriptions.map((sub, index) => {
                const SubIcon = categoryIcons[sub.category] || CreditCard;
                return (
                  <Card 
                    key={sub.service_key || index}
                    className={`p-4 border transition-all ${
                      isDark ? 'bg-white/5 border-white/10 hover:border-[#81d8d0]/40' : 'bg-white border-gray-200 hover:border-[#81d8d0]/60'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                        <SubIcon className="text-[#81d8d0]" size={24} />
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{sub.name}</h4>
                        <p className={`text-sm ${isDark ? 'text-[#64748B]' : 'text-gray-500'}`}>
                          {sub.category} • {sub.source === 'manual' ? 'Ajouté manuellement' : sub.source === 'gmail' ? 'Détecté via Gmail' : 'Exemple de démonstration'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#81d8d0]">{sub.detected_amount?.toFixed(2)}€</p>
                        <p className={`text-xs ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>/mois</p>
                      </div>
                      <button
                        onClick={() => handleDeleteSubscription(sub.service_key)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDark ? 'hover:bg-[#FF6B6B]/20 text-[#FF6B6B]' : 'hover:bg-red-50 text-red-500'
                        }`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className={`p-5 border ${isDark ? 'bg-[#FF6B6B]/5 border-[#FF6B6B]/20' : 'bg-[#FF6B6B]/5 border-[#FF6B6B]/20'}`}>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#FF6B6B] mt-0.5" />
              <div>
                <h4 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Le savais-tu ?</h4>
                <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                  En moyenne, les Français dépensent 200€/mois en abonnements. Skipya t'aide à identifier ceux que tu n'utilises plus.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AbonnementsPage;
